/* eslint-disable react-refresh/only-export-components */
import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'

import { cn } from 'components/lib/utils'

const variantClasses = {
  default: 'bg-core-primary text-core-on-primary hover:bg-core-primary/90',
  destructive: 'bg-core-error text-core-on-error hover:bg-core-error/90',
  outline:
    'outline outline-1 outline-outline-light bg-surface-bright hover:bg-surface text-on-surface',
  secondary:
    'bg-core-secondary text-core-on-secondary hover:bg-core-secondary/80',
  ghost: 'text-on-surface hover:bg-surface',
  link: 'text-on-surface underline-offset-4 hover:underline',
} as const

const sizeClasses = {
  default: 'h-10 px-4 py-2',
  sm: 'h-9 rounded-md px-3',
  lg: 'h-11 rounded-md px-8',
  icon: 'h-10 w-10',
} as const

type ButtonVariant = keyof typeof variantClasses
type ButtonSize = keyof typeof sizeClasses

function buttonVariants({
  variant = 'default',
  size = 'default',
  className,
}: {
  variant?: ButtonVariant | null
  size?: ButtonSize | null
  className?: string
} = {}) {
  // BL-4176: shadcn's default focus-visible:outline-none + ring-* removed.
  // Focus indicators flow through .focus-ring-* classes (see index.css) and
  // the global button:focus-visible base rule in styles/colors.css. Don't
  // restore those defaults without coordinating with the design system.
  return cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-label-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    variantClasses[variant ?? 'default'],
    sizeClasses[size ?? 'default'],
    className
  )
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  variant?: ButtonVariant | null
  size?: ButtonSize | null
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={buttonVariants({ variant, size, className })}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
